import React, { useState } from 'react';
import { getApiUrl } from '@/api/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ShieldCheck, ShieldX } from 'lucide-react';

function apiBase() {
  return getApiUrl() || (typeof window !== 'undefined' ? window.location.origin : '');
}

export default function AssinaValidarPage() {
  const [envelopeId, setEnvelopeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const validar = async () => {
    const id = envelopeId.trim();
    if (!id) {
      setError('Informe a identificação do envelope.');
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(`${apiBase()}/api/assina/public/validar/${encodeURIComponent(id)}`, {
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Não foi possível consultar.');
        return;
      }
      setResult(data);
    } catch {
      setError('Não foi possível conectar ao servidor.');
    } finally {
      setLoading(false);
    }
  };

  const cert = result?.valid ? result.certificate : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200/90 px-4 py-10">
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-8 text-center">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">Assina / Auction</h1>
          <p className="mt-1 text-sm text-slate-600">Validação pública de assinatura eletrônica</p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="space-y-2">
            <Label htmlFor="envelope-id">Identificação do envelope</Label>
            <Input
              id="envelope-id"
              placeholder="Ex.: 67dd076c-ba3a-4cbd-b92f-f9e20038e584"
              value={envelopeId}
              onChange={(e) => setEnvelopeId(e.target.value)}
              className="font-mono text-sm"
              autoComplete="off"
              onKeyDown={(e) => e.key === 'Enter' && validar()}
            />
          </div>
          <Button className="mt-4 w-full" onClick={validar} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Validar'}
          </Button>
        </div>

        {error && (
          <p className="mt-6 text-center text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        {result && !error && (
          <div className="mt-8 space-y-6">
            <div
              className={`flex items-center justify-center gap-2 text-center text-sm font-medium ${
                result.valid ? 'text-emerald-800' : 'text-amber-900'
              }`}
            >
              {result.valid ? (
                <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
              ) : (
                <ShieldX className="h-5 w-5 shrink-0 text-amber-700" aria-hidden />
              )}
              <span>{result.message}</span>
            </div>

            {cert && (
              <div
                className="relative overflow-hidden rounded-lg border-2 border-amber-200/90 bg-gradient-to-b from-amber-50/95 via-white to-slate-50/80 px-6 py-8 shadow-md"
                style={{
                  boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08), inset 0 0 0 1px rgba(255, 255, 255, 0.6)',
                }}
              >
                <div
                  className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full border border-amber-300/40 bg-amber-100/30"
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute -bottom-6 -left-6 h-24 w-24 rounded-full border border-slate-200/80 bg-slate-100/40"
                  aria-hidden
                />

                <div className="relative text-center">
                  <p className="font-serif text-lg font-bold uppercase tracking-wide text-slate-900">
                    Documento assinado eletronicamente
                  </p>
                  <p className="mt-1 text-sm font-semibold text-blue-950/90">Assina / Auction</p>
                  <div className="mx-auto my-5 h-px max-w-[12rem] bg-gradient-to-r from-transparent via-amber-400/80 to-transparent" />
                </div>

                <dl className="relative space-y-3 text-sm text-slate-800">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Última atualização (Brasília)</dt>
                    <dd className="mt-0.5 font-medium tabular-nums">{cert.ultima_atualizacao}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Identificação do envelope</dt>
                    <dd className="mt-0.5 break-all font-mono text-xs">{cert.envelope_id}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Título</dt>
                    <dd className="mt-0.5 [overflow-wrap:anywhere]">{cert.titulo || '(sem título)'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Empresa</dt>
                    <dd className="mt-0.5 [overflow-wrap:anywhere]">{cert.empresa_nome}</dd>
                  </div>
                </dl>

                {cert.signatarios?.length > 0 && (
                  <div className="relative mt-6 border-t border-amber-200/70 pt-5">
                    <p className="mb-3 text-center text-xs font-bold uppercase tracking-widest text-blue-950/80">
                      Assinaturas
                    </p>
                    <ul className="space-y-3 text-sm">
                      {cert.signatarios.map((s, i) => (
                        <li
                          key={`${s.email}-${i}`}
                          className="rounded-md border border-slate-200/80 bg-white/60 px-3 py-2 text-slate-800"
                        >
                          <p className="font-semibold [overflow-wrap:anywhere]">{s.nome}</p>
                          <p className="text-xs text-slate-600 [overflow-wrap:anywhere]">{s.email}</p>
                          {s.assinado_em && (
                            <p className="mt-1 text-xs tabular-nums text-slate-500">Assinado em: {s.assinado_em}</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <p className="mt-10 text-center text-xs text-slate-500">
          Esta página não acessa o sistema Auction; serve apenas para consulta de validade.
        </p>
      </div>
    </div>
  );
}
